import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import "../ads/ad-form.css";

/** Duration in seconds of a local video file (resolves null if unreadable). */
export function readVideoDuration(file) {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => resolve(video.duration);
    video.onerror = () => resolve(null);
    video.src = URL.createObjectURL(file);
  });
}

/**
 * Current JW media (player) of a content, with the upload of a new file.
 *
 * `upload(file, report)` performs the upload and resolves when the media is
 * linked; it may call `report.onStart(totalParts)`, `report.onPart(done)`,
 * `report.onProgress(percent)` and `report.onProcessing()`.
 * `onFileSelected(file)` lets the page read the file (e.g. its duration).
 * `note` is shown under the player (e.g. a processing check in progress).
 */
const MediaUploadCard = ({
  title,
  mediaId,
  upload,
  disabled,
  onFileSelected,
  note,
  successKey,
  failureKey,
}) => {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [replacing, setReplacing] = useState(false);
  const [progress, setProgress] = useState(null); // { total, done, percent, processing }

  const uploading = progress !== null;
  const inputId = `upload-${title.replace(/\W+/g, "-").toLowerCase()}`;

  const start = async () => {
    setProgress({ total: 0, done: 0, percent: 0, processing: false });
    const update = (changes) =>
      setProgress((current) => current && { ...current, ...changes });
    try {
      await upload(file, {
        onStart: (total) => update({ total }),
        onPart: (done) => update({ done, percent: 0 }),
        onProgress: (percent) => update({ percent }),
        onProcessing: () => update({ processing: true }),
      });
      toast.success(t(successKey));
      setFile(null);
      setReplacing(false);
    } catch (error) {
      console.error(error);
      // Reason given by JW Player (e.g. invalid metadata), when there is one
      const detail = error?.response?.data?.message;
      toast.error(detail ? `${t(failureKey)} : ${detail}` : t(failureKey));
    } finally {
      setProgress(null);
    }
  };

  const showPlayer = mediaId && !replacing && !uploading;
  return (
    <section className="ui-card ad-video">
      <h2 className="ad-video-title">{title}</h2>
      {showPlayer ? (
        <div className="ad-video-player">
          <iframe
            src={`https://cdn.jwplayer.com/players/${mediaId}-4t00MwmP.html`}
            frameBorder="0"
            allowFullScreen
            title={title}
          />
        </div>
      ) : uploading ? (
        <div className="ad-dropzone media-progress" aria-live="polite">
          <span className="ad-spinner" aria-hidden="true" />
          <strong>
            {progress.processing
              ? t("Processing by JW Player…")
              : progress.total > 1
              ? t("Part {{done}} / {{total}} · {{percent}} %", progress)
              : t("Uploading...")}
          </strong>
          {!progress.processing && progress.total > 0 && (
            <span className="media-progress-bar">
              <span
                style={{
                  width: `${Math.round(
                    ((progress.done + progress.percent / 100) / progress.total) *
                      100
                  )}%`,
                }}
              />
            </span>
          )}
          <span className="ui-hint">{t("Keep this page open")}</span>
        </div>
      ) : (
        <label className="ad-dropzone" htmlFor={inputId} data-disabled={disabled || undefined}>
          <i className="ri-upload-cloud-line" aria-hidden="true"></i>
          <strong>{file ? file.name : t("upload video")}</strong>
          <span className="ui-hint">MP4</span>
          <input
            id={inputId}
            type="file"
            accept="video/mp4,video/x-m4v,video/*"
            className="sr-only"
            disabled={disabled}
            onChange={(e) => {
              const picked = e.target.files && e.target.files[0];
              setFile(picked || null);
              if (picked && onFileSelected) onFileSelected(picked);
              e.target.value = "";
            }}
          />
        </label>
      )}
      {showPlayer && note && (
        <p className="ui-hint media-note" aria-live="polite">
          <span className="ad-spinner" aria-hidden="true" />
          {note}
        </p>
      )}
      {!disabled && (
        <div className="ad-video-actions">
          {showPlayer ? (
            <button
              type="button"
              className="ui-btn ui-btn-secondary"
              onClick={() => setReplacing(true)}
            >
              <i className="ri-refresh-line" aria-hidden="true"></i>
              {t("Reupload")}
            </button>
          ) : (
            <>
              {(replacing || file) && (
                <button
                  type="button"
                  className="ui-btn ui-btn-secondary"
                  disabled={uploading}
                  onClick={() => {
                    setReplacing(false);
                    setFile(null);
                  }}
                >
                  {t("cancel")}
                </button>
              )}
              <button
                type="button"
                className="ui-btn"
                disabled={!file || uploading}
                onClick={start}
              >
                {t("Upload")}
              </button>
            </>
          )}
        </div>
      )}
    </section>
  );
};

export default MediaUploadCard;
